"use client";

import { useState } from "react";
import { motion } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";
import SmbPlansDropdown from "@/components/SmbPlansDropdown";
import { useCurrency } from "@/lib/useCurrency";
import {
  contactHref,
  contactLabel,
  formatMoney,
  formatRate,
  getPlanPricing,
  resolveCheckoutUrl,
  YEARLY_DISCOUNT_PCT,
  type BillingInterval,
  type DisplayPlanKey,
} from "@/lib/constants/pricing-display";

/**
 * All display amounts (prices, overage rates, the yearly discount, setup
 * fees) come from src/lib/constants/pricing-display.ts; do not hardcode
 * dollar amounts in this component. SMB checkout URLs also live there (the
 * hardcoded live USD Stripe links + env-driven CAD links). The MONTHLY
 * Business and Enterprise USD Stripe Payment Links below are intentionally
 * hardcoded and unchanged; they are passed to resolveCheckoutUrl as the native
 * monthly override. Every other link comes from the two-currency map, with a
 * "Contact us for [USD|CAD] billing" fallback whenever a link is empty (which
 * is always the case for CAD until those Stripe links are created).
 */
const plans: Array<{
  key: DisplayPlanKey | null;
  title: string;
  tagline: string;
  hasPrice: boolean;
  bestFor: string;
  description: string;
  /** Static feature lines; the overage line is injected dynamically. */
  features: string[];
  featured: boolean;
  cta: string;
  stripeNote: string | null;
  /** Native (USD) hardcoded monthly link; contact CTA for the Corporate plan. */
  stripeUrl: string;
}> = [
  {
    key: "business",
    title: "Business Plan",
    tagline: "Scale your capabilities",
    hasPrice: true,
    bestFor: "Best for growing teams",
    description:
      "Scale your capabilities with a plan built for growing teams that want multilingual voice outreach, predictable monthly access, and a more self-directed operating model.",
    features: [
      "Includes 100 minutes per month",
      "Designed for growing teams and scaling",
      "Communicate in 10+ languages",
      "Up to 5 voice AI agents",
      "Standard reports with CSV exports",
      "Limited analytics",
      "Priority email support (24-hour response)",
      "Shared responsibility model. Tools provided, client manages inputs",
      "Lower minute pricing unlocked through subscription",
      "Reduces manual outreach and improves engagement",
    ],
    featured: false,
    cta: "Start Business Plan",
    stripeNote: "Stripe Checkout",
    stripeUrl: "https://buy.stripe.com/3cIbJ3duD2UC7oE5pzew80M", // Business Plan ($699/mo + $300 one-time setup, v5 2026-07-07)
  },
  {
    key: "enterprise",
    title: "Enterprise Plan",
    tagline: "Do more with enterprise",
    hasPrice: true,
    bestFor: "Best for complex or high-volume projects",
    description:
      "Support larger-scale voice operations with lower overage pricing, more included minutes, advanced reporting, and dedicated hands-on project oversight.",
    features: [
      "Includes 250 minutes per month",
      "Full-scale solution for larger operations",
      "Communicate in 55+ languages",
      "Up to 10 voice AI agents",
      "Advanced analytics, multilingual support, and compliance features",
      "Dedicated project manager with end-to-end oversight. Priced per engagement.",
      "Results delivered through your reporting workflow",
      "Custom campaign design and optimization included",
      "Premium support with direct line access",
      "End-to-end project management. No staffing headaches",
      "Optimized for higher-volume projects with lower minute pricing",
      "AI Campaign Intelligence Reports (included)",
    ],
    featured: true,
    cta: "Start Enterprise Plan",
    stripeNote: "Stripe Checkout",
    stripeUrl: "https://buy.stripe.com/14A8wReyH66OaAQ8BLew80O", // Enterprise Plan ($999/mo + $300 one-time setup, v5 2026-07-07)
  },
  {
    key: null,
    title: "Corporate Plan",
    tagline: "Fully customizable for your organization",
    hasPrice: false,
    bestFor: "Best for organizations requiring full control",
    description:
      "A fully customizable deployment with white-label branding, dedicated infrastructure, and a bespoke feature set tailored entirely to your organization's needs.",
    features: [
      "Up to 20 voice AI agents",
      "White-label branding and full customization",
      "Dedicated infrastructure and custom integrations",
      "Everything in Enterprise, plus bespoke features",
      "Custom minute and overage pricing",
      "Unlimited team members",
      "Full access to call recording downloads",
      "Complete call transcripts for every session",
      "Dedicated account executive and engineering support",
      "Custom SLAs and compliance packages",
    ],
    featured: false,
    cta: "Contact Sales",
    stripeNote: null,
    stripeUrl: "/contact?subject=Corporate%20Plan%20Inquiry",
  },
];

/** Segmented Monthly / Yearly toggle, styled to match the pricing cards. */
function BillingToggle({
  value,
  onChange,
}: {
  value: BillingInterval;
  onChange: (next: BillingInterval) => void;
}) {
  const options: Array<{ key: BillingInterval; label: string }> = [
    { key: "monthly", label: "Monthly" },
    { key: "yearly", label: `Yearly (Save ${YEARLY_DISCOUNT_PCT}%)` },
  ];
  return (
    <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
      <div
        role="group"
        aria-label="Billing interval"
        style={{
          display: "inline-flex",
          gap: 4,
          padding: 4,
          background: "var(--color-bg)",
          border: "1px solid var(--color-border)",
          borderRadius: 999,
        }}
      >
        {options.map((opt) => {
          const active = value === opt.key;
          return (
            <button
              key={opt.key}
              type="button"
              aria-pressed={active}
              onClick={() => onChange(opt.key)}
              style={{
                fontFamily: "var(--font-body)",
                fontSize: "0.85rem",
                fontWeight: active ? 600 : 400,
                padding: "10px 20px",
                borderRadius: 999,
                border: "none",
                cursor: "pointer",
                background: active ? "var(--color-text)" : "transparent",
                color: active ? "#fff" : "var(--color-text-muted)",
                transition: "background 0.2s ease, color 0.2s ease",
              }}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function Pricing() {
  const [billing, setBilling] = useState<BillingInterval>("monthly");
  // Currency is now chosen via the header flag switch and the first-visit
  // welcome modal; this component only reads the current value (both write
  // through the same useCurrency hook, so prices react to those changes).
  const { currency } = useCurrency();
  const yearly = billing === "yearly";

  return (
    <section id="pricing" className="section-padding" style={{ background: "var(--color-bg-alt)" }}>
      <div className="section-inner" style={{ maxWidth: 1200 }}>
        <h2 className="section-heading">
          Choose the plan that{" "}
          <em style={{ fontFamily: "var(--font-heading)" }}>fits your business or call center</em>
        </h2>
        <p className="section-subheading">
          Monthly or annual billing (save {YEARLY_DISCOUNT_PCT}% on annual). Clear pricing. No
          hidden fees. All prices in {currency}. No setup fee on Small Business plans.
        </p>

        {/* Monthly / Yearly billing toggle. Currency selection now lives in the
            header flag switch + first-visit welcome modal. */}
        <BillingToggle value={billing} onChange={setBilling} />

        {/* Small Business plans, shown first, above the larger plans */}
        <SmbPlansDropdown interval={billing} currency={currency} />

        {/* Business & Enterprise plans caption */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 4,
            textAlign: "center",
            marginBottom: 32,
          }}
        >
          <h3
            style={{
              fontFamily: "var(--font-heading)",
              fontSize: "1.5rem",
              color: "var(--color-text)",
              margin: 0,
            }}
          >
            Business &amp; Enterprise Plans
          </h3>
          <p
            style={{
              fontSize: "0.88rem",
              fontWeight: 300,
              color: "var(--color-text-muted)",
              margin: 0,
            }}
          >
            Full-scale voice AI for research firms and call centers.
          </p>
        </div>

        {/* Plan cards */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(min(320px, 100%), 1fr))",
            gap: 24,
            marginBottom: 40,
          }}
        >
          {plans.map((plan) => {
            const display = plan.key ? getPlanPricing(currency, plan.key) : null;
            // Resolve the live checkout link for the current currency+interval.
            // For USD, the hardcoded monthly Stripe link is the native override;
            // for CAD (and empty yearly links) we fall back to a contact CTA so
            // we never send a foreign-currency price to a native checkout.
            const ctaUrl = plan.key
              ? resolveCheckoutUrl(currency, plan.key, billing, plan.stripeUrl)
              : plan.stripeUrl;
            const linkMissing = plan.hasPrice && !ctaUrl;
            const ctaHref = ctaUrl ?? contactHref(currency);
            const ctaLabel = linkMissing ? contactLabel(currency) : plan.cta;
            const ctaExternal = ctaHref.startsWith("http");
            const showStripeNote = Boolean(plan.stripeNote) && !linkMissing;

            // Inject the currency-aware overage line as the second feature.
            const features =
              display !== null
                ? [
                    plan.features[0],
                    `${formatRate(display.overage)} ${currency} per additional minute`,
                    ...plan.features.slice(1),
                  ]
                : plan.features;

            return (
              <motion.div
                key={plan.title}
                className={`pricing-card${plan.featured ? " featured" : ""}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                {/* Best for badge */}
                <span
                  style={{
                    display: "inline-block",
                    fontFamily: "var(--font-body)",
                    fontSize: "0.72rem",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    color: plan.featured ? "var(--color-accent)" : "var(--color-text-muted)",
                    marginBottom: 12,
                  }}
                >
                  {plan.bestFor}
                </span>

                <h3
                  style={{
                    fontFamily: "var(--font-heading)",
                    fontSize: "1.5rem",
                    color: "var(--color-text)",
                    marginBottom: 4,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  {plan.title}
                </h3>
                <p
                  style={{
                    fontSize: "0.88rem",
                    color: "var(--color-text-muted)",
                    fontWeight: 300,
                    marginBottom: 20,
                  }}
                >
                  {plan.tagline}
                </p>

                {/* Price */}
                <div style={{ marginBottom: 20 }}>
                  {plan.hasPrice && display ? (
                    <>
                      <span
                        style={{
                          fontFamily: "var(--font-heading)",
                          fontSize: "2.8rem",
                          color: "var(--color-text)",
                          lineHeight: 1,
                        }}
                      >
                        {formatMoney(yearly ? display.yearlyPerMonth : display.price, currency)}
                      </span>
                      <span
                        style={{
                          fontSize: "0.88rem",
                          color: "var(--color-text-muted)",
                          fontWeight: 300,
                          marginLeft: 6,
                        }}
                      >
                        {currency} / month
                      </span>
                      <p
                        style={{
                          fontSize: "0.75rem",
                          color: "var(--color-text-muted)",
                          fontWeight: 300,
                          margin: "8px 0 0",
                        }}
                      >
                        {yearly
                          ? `${formatMoney(display.yearlyTotal, currency)} ${currency} billed annually (save ${YEARLY_DISCOUNT_PCT}%)`
                          : "Billed monthly"}
                        {display.setupFee > 0 &&
                          ` · plus a one-time ${formatMoney(display.setupFee, currency)} ${currency} setup fee`}
                      </p>
                    </>
                  ) : (
                    <span
                      style={{
                        fontFamily: "var(--font-heading)",
                        fontSize: "1.8rem",
                        color: "#6366f1",
                        lineHeight: 1,
                      }}
                    >
                      Custom Pricing
                    </span>
                  )}
                </div>

                <p
                  style={{
                    fontSize: "0.88rem",
                    color: "var(--color-text-muted)",
                    fontWeight: 300,
                    lineHeight: 1.7,
                    marginBottom: 28,
                    paddingBottom: 24,
                    borderBottom: "1px solid var(--color-border)",
                  }}
                >
                  {plan.description}
                </p>

                {/* Features */}
                <ul style={{ listStyle: "none", padding: 0, marginBottom: 32 }}>
                  {features.map((f) => (
                    <li
                      key={f}
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 10,
                        marginBottom: 12,
                        fontSize: "0.88rem",
                        color: "var(--color-text)",
                        fontWeight: 400,
                        lineHeight: 1.5,
                      }}
                    >
                      <Check
                        size={16}
                        style={{ flexShrink: 0, marginTop: 3 }}
                        color={plan.featured ? "var(--color-accent)" : "var(--color-text-muted)"}
                        strokeWidth={2}
                      />
                      {f}
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <a
                  href={ctaHref}
                  target={ctaExternal ? "_blank" : undefined}
                  rel={ctaExternal ? "noopener noreferrer" : undefined}
                  className="btn-primary"
                  style={{
                    width: "100%",
                    justifyContent: "center",
                    background: plan.featured ? "var(--color-text)" : "transparent",
                    color: plan.featured ? "#fff" : "var(--color-text)",
                    border: plan.featured ? "none" : "1.5px solid var(--color-border)",
                    padding: "16px 24px",
                  }}
                >
                  {ctaLabel}
                  <ArrowRight size={16} />
                </a>
                {showStripeNote && (
                  <p
                    style={{
                      textAlign: "center",
                      marginTop: 10,
                      fontSize: "0.75rem",
                      color: "var(--color-text-muted)",
                      fontWeight: 300,
                    }}
                  >
                    Powered by {plan.stripeNote}
                  </p>
                )}
                {!plan.hasPrice && !plan.stripeNote && (
                  <p
                    style={{
                      textAlign: "center",
                      marginTop: 10,
                      fontSize: "0.75rem",
                      color: "var(--color-text-muted)",
                      fontWeight: 300,
                    }}
                  >
                    Tailored to your requirements
                  </p>
                )}
              </motion.div>
            );
          })}
        </div>

        {/* Comparison row */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
            gap: 16,
            maxWidth: 900,
            margin: "0 auto 32px",
          }}
        >
          {[
            {
              plan: "Business Plan",
              details: `100 included minutes · ${formatRate(getPlanPricing(currency, "business").overage)} ${currency}/min overage`,
            },
            {
              plan: "Enterprise Plan",
              details: `250 included minutes · ${formatRate(getPlanPricing(currency, "enterprise").overage)} ${currency}/min overage`,
            },
            { plan: "Corporate Plan", details: "Custom minutes · Custom overage rates" },
          ].map((p) => (
            <div
              key={p.plan}
              style={{
                background: "var(--color-bg)",
                border: "1px solid var(--color-border)",
                borderRadius: 12,
                padding: "16px",
                textAlign: "center",
                overflowWrap: "break-word",
              }}
            >
              <p style={{ fontWeight: 600, fontSize: "0.82rem", marginBottom: 4 }}>{p.plan}</p>
              <p style={{ fontSize: "0.8rem", color: "var(--color-text-muted)", fontWeight: 300 }}>
                {p.details}
              </p>
            </div>
          ))}
        </motion.div>

        {/* Pricing note */}
        <p
          style={{
            textAlign: "center",
            fontSize: "0.85rem",
            color: "var(--color-text-muted)",
            fontWeight: 300,
            lineHeight: 1.7,
            maxWidth: 680,
            margin: "0 auto",
          }}
        >
          As your usage grows, your effective cost per minute drops. Higher plans
          are designed to support bigger projects with stronger economics and
          deeper support.
        </p>
      </div>
    </section>
  );
}
